import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex } from '../utils/constants';
import { HeadTracker } from '../systems/HeadTracker';
import { eventBus } from '../systems/EventBus';
import { soundManager } from '../systems/SoundManager';
import { showInput, hideAllInputs } from '../utils/inputs';

const PLAYER_NAME_KEY = 'neuro_duel_player_name';

export default class CameraSetupScene extends Phaser.Scene {
  private headTracker: HeadTracker | null = null;
  private statusText!: Phaser.GameObjects.Text;
  private permissionGranted = false;
  private continueBtn!: Phaser.GameObjects.Container;
  private playerName = '';
  private useCamera = true;
  private useKeyboard = true;
  private camCheckbox!: Phaser.GameObjects.Container;
  private kbCheckbox!: Phaser.GameObjects.Container;
  private warningText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'CameraSetupScene' });
  }

  create() {
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    this.playerName = localStorage.getItem(PLAYER_NAME_KEY) || '';

    this.add.text(GAME_W / 2, 80, '神经决斗', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif',
      fontSize: '28px',
      color: intToHex(COLORS.cyan),
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const nameLabel = this.add.text(GAME_W / 2, 120, '输入你的名字', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '14px',
      color: intToHex(COLORS.gray),
    }).setOrigin(0.5);

    const nameBg = this.add.rectangle(GAME_W / 2, 150, 300, 36, 0x000000, 0.6).setStrokeStyle(1, COLORS.cyan);
    const nameText = this.add.text(GAME_W / 2, 150, this.playerName || '点击输入名字', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: this.playerName ? intToHex(COLORS.white) : '#555577',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    nameText.on('pointerdown', () => {
      showInput('nd-input-name', GAME_W / 2, 150, (val) => {
        if (val && val.trim().length > 0) {
          this.playerName = val.trim().substring(0, 12);
          localStorage.setItem(PLAYER_NAME_KEY, this.playerName);
          nameText.setText(this.playerName).setColor(intToHex(COLORS.white));
        }
      });
    });

    this.add.text(GAME_W / 2, 200, '摄像头校准', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '36px',
      color: intToHex(COLORS.cyan),
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(GAME_W / 2, 240, '请允许摄像头权限以启用体感控制', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '18px',
      color: intToHex(COLORS.gray),
    }).setOrigin(0.5);

    // Input selection
    this.add.text(GAME_W / 2, 290, '选择输入方式', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '16px',
      color: intToHex(COLORS.cyan),
    }).setOrigin(0.5);

    this.camCheckbox = this.createCheckbox(GAME_W / 2 - 130, 330, '摄像头识别输入');
    this.kbCheckbox = this.createCheckbox(GAME_W / 2 + 130, 330, '键盘输入');

    this.warningText = this.add.text(GAME_W / 2, 375, '至少选择一种输入方式', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '14px',
      color: intToHex(COLORS.red),
    }).setOrigin(0.5).setAlpha(0);

    this.statusText = this.add.text(GAME_W / 2, 430, '等待摄像头权限...', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '20px',
      color: intToHex(COLORS.yellow),
    }).setOrigin(0.5);

    this.continueBtn = this.createButton('继续', COLORS.cyan, () => this.goNext());

    this.events.on('shutdown', hideAllInputs);

    this.initCamera();
  }

  private createCheckbox(x: number, y: number, label: string): Phaser.GameObjects.Container {
    const boxSize = 22;
    const isCam = label === '摄像头识别输入';

    const box = this.add.rectangle(0, 0, boxSize, boxSize, 0x000000, 0).setStrokeStyle(2, COLORS.cyan);
    const fill = this.add.rectangle(0, 0, boxSize - 4, boxSize - 4, COLORS.cyan);
    fill.setAlpha(isCam ? (this.useCamera ? 1 : 0) : (this.useKeyboard ? 1 : 0));

    const text = this.add.text(16, 0, label, {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '15px',
      color: intToHex(COLORS.white),
    }).setOrigin(0, 0.5);

    const container = this.add.container(x, y, [box, fill, text]);
    container.setSize(220, 36);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerdown', () => {
      this.toggleCheckbox(container, fill, isCam);
    });

    return container;
  }

  private toggleCheckbox(container: Phaser.GameObjects.Container, fill: Phaser.GameObjects.Rectangle, isCam: boolean) {
    const currentlyChecked = isCam ? this.useCamera : this.useKeyboard;

    if (currentlyChecked) {
      const otherChecked = isCam ? this.useKeyboard : this.useCamera;
      if (!otherChecked) {
        this.warningText.setAlpha(1);
        return;
      }
    }

    this.warningText.setAlpha(0);

    if (isCam) {
      this.useCamera = !currentlyChecked;
    } else {
      this.useKeyboard = !currentlyChecked;
    }

    fill.setAlpha(currentlyChecked ? 0 : 1);
  }

  private goNext() {
    if (!this.useCamera && !this.useKeyboard) {
      this.warningText.setAlpha(1);
      return;
    }

    this.cameras.main.fadeOut(500, 0, 0, 30);
    this.time.delayedCall(500, () => {
      this.scene.start('CalibrationScene', {
        headTracker: this.useCamera ? this.headTracker : null,
        keyboardOnly: !this.useCamera,
        useCamera: this.useCamera,
        useKeyboard: this.useKeyboard,
        playerName: this.playerName,
      });
    });
  }

  private async initCamera() {
    this.headTracker = new HeadTracker(this);
    const success = await this.headTracker.init();

    if (success) {
      this.permissionGranted = true;
      this.statusText.setText('摄像头已就绪！请看向屏幕中心。').setColor(intToHex(COLORS.green));
    } else if (this.useCamera) {
      this.statusText.setText('无法访问摄像头，你可以继续使用键盘输入').setColor(intToHex(COLORS.yellow));
    } else {
      this.statusText.setText('无法访问摄像头').setColor(intToHex(COLORS.red));
    }
  }

  private createButton(text: string, color: number, callback: () => void): Phaser.GameObjects.Container {
    const w = 280;
    const h = 48;
    const group = this.add.container(GAME_W / 2, GAME_H / 2 + 180, [
      this.add.rectangle(0, 0, w, h, color).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, w, h, 0x000000).setOrigin(0.5).setStrokeStyle(2, color),
      this.add.text(0, 0, text, {
        fontFamily: 'Microsoft YaHei, sans-serif',
        fontSize: '18px',
        color: intToHex(color),
        fontStyle: 'bold',
      }).setOrigin(0.5),
    ]);

    group.setSize(w, h);
    group.setInteractive({ useHandCursor: true });
    group.on('pointerover', () => {
      (group.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4);
    });
    group.on('pointerout', () => {
      (group.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2);
    });
    group.on('pointerdown', callback);

    return group;
  }
}
